import { Breadcrumb, Button, Col, DatePicker, Empty, Form, Input, Row, Select, Spin } from 'antd';
import gql from 'graphql-tag';
import * as moment from 'moment';
import React, { FormEvent, useState } from 'react';
import { Query } from 'react-apollo';
import { Link } from 'react-router-dom';
import * as xlsx from 'xlsx';

import { Panel } from '../../components/Panel';
import { SectionMetric, StatisticInterval } from './type';

const STATISTIC_INTERVAL = {
  [StatisticInterval.NONE]: '无',
  [StatisticInterval.HOUR]: '时',
  [StatisticInterval.DAY]: '天',
  [StatisticInterval.WEEK]: '一周',
  [StatisticInterval.TWO_WEEKS]: '两周',
  [StatisticInterval.MONTH]: '月',
  [StatisticInterval.QUARTER]: '季度',
  [StatisticInterval.HALF_YEAR]: '半年',
  [StatisticInterval.YEAR]: '一年',
};

const FormItem = Form.Item;
const SECTION_METRICS_QUERY = gql`
  query getSectionMetrics(
    $period: StatisticPeriod!
    $course_id: String
    $lesson_type: String
    $lesson_id: String
    $mode: String
    $section_id: String
    $level: String
  ) {
    statistic {
      sectionMetrics(
        period: $period
        course_id: $course_id
        lesson_type: $lesson_type
        lesson_id: $lesson_id
        mode: $mode
        section_id: $section_id
        level: $level
      ) {
        key
        course_id
        lesson_type
        lesson_id
        mode
        section_id
        level
        duration_avg
        app_pause_avg
        app_resume_avg
        lesson_quit_avg
        section_start
        section_complete_rate
        level_start
        level_pass_rate
        level_fail_rate
        question_start
        question_pass_rate
        question_fail_rate
        operation_start
        operation_end
        operation_correct_avg
        operation_wrong_avg
        operation_wrong_longest_avg
        operation_wrong_longest_med
        operation_neutral_avg
        operation_timeout_avg
        ui_next_appear
        ui_next_click_rate
        from
        to
      }
    }
  }
`;

export const SectionMetrics = () => {
  const [variables, setVariables] = useState({
    from: new Date(),
    to: new Date(),
    interval: StatisticInterval.MONTH,
    courseId: undefined,
    lessonType: undefined,
    lessonId: undefined,
    mode: undefined,
    sectionId: undefined,
    level: undefined,
  });
  const [period, setPeriod] = useState({ from: new Date(), to: new Date() });
  const [interval, setInterval] = useState(StatisticInterval.MONTH);
  const [courseId, setCourseId] = useState();
  const [lessonType, setLessonType] = useState();
  const [lessonId, setLessonId] = useState();
  const [mode, setMode] = useState();
  const [sectionId, setSectionId] = useState();
  const [level, setLevel] = useState();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const exportExcel = (sectionMetrics: SectionMetric[]) => {
    const workbook = xlsx.utils.book_new();
    const sheet1 = xlsx.utils.json_to_sheet(sectionMetrics);
    xlsx.utils.book_append_sheet(workbook, sheet1);

    xlsx.writeFile(workbook, 'sectionMetrics.xlsx');

    setIsSubmitting(false);
  };

  const search = (e: FormEvent) => {
    if (isSubmitting) {
      return;
    }
    setVariables({ ...period, interval, courseId, lessonType, lessonId, mode, sectionId, level });

    setIsSubmitting(true);

    e.preventDefault();
  };

  const onCourseIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCourseId(e.target.value);
  };

  const onLessonIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLessonId(e.target.value);
  };

  const onSectionIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSectionId(e.target.value);
  };

  const onLevelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLevel(e.target.value);
  };

  return (
    <>
      <Breadcrumb style={{ marginBottom: 10, fontSize: 13 }}>
        <Breadcrumb.Item>
          <Link to={`/Home`}>首页</Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>环节关卡统计</Breadcrumb.Item>
      </Breadcrumb>
      <Query
        query={SECTION_METRICS_QUERY}
        variables={{
          period: { from: variables.from, to: variables.to, interval: variables.interval },
          course_id: variables.courseId,
          lesson_type: variables.lessonType,
          lesson_id: variables.lessonId,
          mode: variables.mode,
          sectionId: variables.sectionId,
          level: variables.level,
        }}
      >
        {({ loading, error, data }) => {
          if (error) return <div>{error.message}</div>;
          if (loading) return <Spin />;
          if (!data || !data.statistic) {
            // @ts-ignore
            return <Empty />;
          }
          const {
            statistic: { sectionMetrics },
          } = data;
          if (isSubmitting) {
            setTimeout(() => {
              exportExcel(sectionMetrics);
            }, 500);
          }

          return (
            <>
              <Panel style={{ padding: 24 }}>
                <Form layout="inline" onSubmit={search}>
                  <Row gutter={24}>
                    <Col xs={24} md={24} lg={24} xl={10} xxl={8}>
                      <FormItem label={`时间:`}>
                        <DatePicker.RangePicker
                          value={[moment(period.from), moment(period.to)]}
                          ranges={{
                            今天: [moment(), moment()],
                            本周: [moment().startOf('week'), moment().endOf('week')],
                            上周: [
                              moment().startOf('week').subtract(1, 'week'),
                              moment().endOf('week').subtract(1, 'week'),
                            ],
                          }}
                          onChange={dates => {
                            const period = {
                              from: moment(dates[0]).startOf('day').toDate(),
                              to: moment(dates[1]).endOf('day').toDate(),
                            };
                            setPeriod(period);
                          }}
                        />
                      </FormItem>
                    </Col>
                    <Col xs={24} md={24} lg={24} xl={7} xxl={8}>
                      <FormItem label={`Course Id:`}>
                        <Input placeholder="请输入Course Id" allowClear value={courseId} onChange={onCourseIdChange} />
                      </FormItem>
                    </Col>
                    <Col xs={24} md={24} lg={24} xl={7} xxl={8}>
                      <FormItem label={`Lesson Id:`}>
                        <Input placeholder="请输入Lesson Id" allowClear value={lessonId} onChange={onLessonIdChange} />
                      </FormItem>
                    </Col>
                  </Row>
                  <Row gutter={24}>
                    <Col xs={24} md={24} lg={24} xl={7} xxl={8}>
                      <FormItem label={`Level:`}>
                        <Input placeholder="请输入Level" allowClear value={level} onChange={onLevelChange} />
                      </FormItem>
                    </Col>
                    <Col xs={24} md={24} lg={24} xl={7} xxl={8}>
                      <FormItem label={`Section Id:`}>
                        <Input
                          placeholder="请输入Section Id"
                          allowClear
                          value={sectionId}
                          onChange={onSectionIdChange}
                        />
                      </FormItem>
                    </Col>
                    <Col xs={24} md={24} lg={24} xl={8} xxl={8}>
                      <FormItem label={`Lesson Type:`}>
                        <Select
                          style={{ width: '200px' }}
                          placeholder="选择Lesson Type"
                          value={lessonType}
                          onChange={(value: string) => {
                            setLessonType(value);
                          }}
                        >
                          <Select.Option key={'R'} value={'R'}>
                            {'R'}
                          </Select.Option>
                          <Select.Option key={'E'} value={'E'}>
                            {'E'}
                          </Select.Option>
                          <Select.Option key={'L'} value={'L'}>
                            {'L'}
                          </Select.Option>
                        </Select>
                      </FormItem>
                    </Col>
                  </Row>
                  <Row gutter={24}>
                    <Col xs={24} md={24} lg={24} xl={8} xxl={8}>
                      <FormItem label={`Mode:`}>
                        <Select
                          style={{ width: '200px' }}
                          value={mode}
                          placeholder="选择Mode"
                          onChange={(value: string) => {
                            setMode(value);
                          }}
                        >
                          <Select.Option key={'S'} value={'S'}>
                            {'S'}
                          </Select.Option>
                          <Select.Option key={'D'} value={'D'}>
                            {'D'}
                          </Select.Option>
                        </Select>
                      </FormItem>
                    </Col>
                    <Col xs={24} md={24} lg={24} xl={8} xxl={8}>
                      <FormItem label={`时间粒度:`}>
                        <Select
                          style={{ width: '200px' }}
                          placeholder="选择时间粒度"
                          defaultValue={StatisticInterval.MONTH}
                          onChange={(value: StatisticInterval) => {
                            setInterval(value);
                          }}
                        >
                          {Object.keys(STATISTIC_INTERVAL).map((interval: StatisticInterval) => {
                            return (
                              <Select.Option
                                key={interval}
                                value={interval}
                                disabled={
                                  ![StatisticInterval.DAY, StatisticInterval.MONTH].some(item => item === interval)
                                }
                              >
                                {STATISTIC_INTERVAL[interval]}
                              </Select.Option>
                            );
                          })}
                        </Select>
                      </FormItem>
                    </Col>
                    <Col xs={24} md={24} lg={24} xl={8} xxl={8}>
                      <Button
                        style={{ marginRight: 8 }}
                        disabled={loading}
                        type="primary"
                        htmlType="submit"
                        icon="download"
                      >
                        导出
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </Panel>
            </>
          );
        }}
      </Query>
    </>
  );
};



// WEBPACK FOOTER //
// ./src/screens/statistic/SectionMetrics.tsx